import { useState } from 'react';
import { useFormikContext } from 'formik';
import { Bot, Pencil } from 'lucide-react';

import { CopyButton } from '@/components/copy-button';
import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownAction } from '@/components/ui/dropdown-actions';
import { Input } from '@/components/ui/input';

import type { AgentUpdateFormValues } from '@/schemas/agent.schema';

import { Permissions } from '@/constants';

import { renderAgentStatusBadge } from '@/lib/badge';

import { DeleteButton } from '../actions/delete';

import type { AgentDetailResponse } from '@/types/agent.type';

type Props = {
  agentDetail: AgentDetailResponse;
};

export const AgentHeader = ({ agentDetail }: Props) => {
  const { values, getFieldProps } = useFormikContext<AgentUpdateFormValues>();
  const [isEditingName, setIsEditingName] = useState(false);

  return (
    <div className="flex items-start justify-between mb-6">
      <div className="flex items-start gap-3">
        <Avatar className="h-8 w-8 bg-primary">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <Bot className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-2 mb-1">
            {isEditingName ? (
              <Input
                {...getFieldProps('agent_name')}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setIsEditingName(false);
                  }
                }}
                autoFocus
                className="text-2xl font-semibold h-8 py-0 px-2 -ml-2 w-[500px]"
              />
            ) : (
              <h1 className="text-2xl font-semibold text-foreground tracking-tight">
                {values?.agent_name || 'Agent'}
              </h1>
            )}

            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setIsEditingName(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {renderAgentStatusBadge(agentDetail?.status || '')}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{agentDetail?.canonical_name}</span>
            <CopyButton
              value={agentDetail?.canonical_name || ''}
              tooltip="Copy name"
              className="h-5 w-5 p-0"
              iconClassName="h-3.5 w-3.5"
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ProtectedComponent requiredPermissions={[Permissions.AgentDelete]}>
          <DropdownAction>
            <DeleteButton agentId={agentDetail?.id} />
          </DropdownAction>
        </ProtectedComponent>
      </div>
    </div>
  );
};
